'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@radix-ui/themes';
import { ApiAttribute } from '@context/openapi/types';

interface InputLabelProps {
  field: ApiAttribute;
  fieldNameOverride?: string;
}

export const InputLabel: React.FC<InputLabelProps> = ({
  field,
  fieldNameOverride,
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLSpanElement>(null);
  const displayName = fieldNameOverride || field.name;

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, []);

  const getDisplayType = (): string => {
    if (field.type === 'FILTER' && field.filterType) {
      const filterType = field.filterType;
      const filterTypeMap: Record<string, string> = {
        StringFilter: 'string filter',
        EnumFilter: 'enum filter',
        TimestampFilter: 'timestamp filter',
        NumberFilter: 'number filter',
        BooleanFilter: 'boolean filter',
      };
      return filterTypeMap[filterType] || `${filterType.toLowerCase()} filter`;
    }

    if (field.type === 'OBJECT') {
      return field.child.length > 0 ? field.type : 'JSON Object';
    }
    return field.type.toLowerCase();
  };

  return (
    <Box className="flex items-center gap-2 flex-1 min-w-0 relative">
      <div className="flex items-center gap-2 w-full h-full">
        <label
          htmlFor={field.key}
          className={`font-inter text-[12px] cursor-pointer flex items-center w-full h-full`}
        >
          <div className="flex items-center max-w-[180px]">
            <span
              ref={textRef}
              className={`whitespace-nowrap overflow-hidden ${
                field?.value && (field?.value as string).length > 0
                  ? 'font-semibold'
                  : ''
              }`}
              style={{ maxWidth: isOverflowing ? 'calc(100% - 20px)' : '100%' }}
            >
              {displayName}
            </span>

            {/* Explicit three dots that appear if content is overflowing */}
            {isOverflowing && (
              <span
                ref={dotsRef}
                className="ml-1 cursor-pointer font-bold"
                onMouseEnter={() => setShowPopover(true)}
                onMouseLeave={() => setShowPopover(false)}
              >
                ...
              </span>
            )}
          </div>

          {field.required && <Text color="red">*</Text>}
          <span className="font-inter text-[12px] italic lowercase ml-2">
            {getDisplayType()}
          </span>
        </label>
      </div>

      {/* Popover with full content that appears on hover */}
      {showPopover && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full mt-1 bg-white border border-gray-300 shadow-lg p-2 rounded text-sm text-gray-700 z-10 w-auto max-w-xs"
          onMouseEnter={() => setShowPopover(true)}
          onMouseLeave={() => setShowPopover(false)}
        >
          {displayName}
        </div>
      )}
    </Box>
  );
};

export default InputLabel;
